import React from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, AlignmentType, ShadingType, ImageRun, ExternalHyperlink } from 'docx';
import logoImage from '@/assets/edugogy-logo.png';

interface ResourceLayoutProps {
  title: string;
  description: string;
  content: React.ReactNode;
  hideMainDownload?: boolean;
}

const ResourceLayout: React.FC<ResourceLayoutProps> = ({ title, description, content, hideMainDownload = false }) => {
  const handleDownloadWord = async () => {
    try {
      const contentElement = document.getElementById('resource-content');
      if (!contentElement) {
        toast.error('Content not found for download');
        return;
      }

      const children = [];

      // Add Edugogy branding header
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "EDUGOGY",
              font: "Arial",
              size: 32,
              bold: true,
              color: "1a73e8",
            }),
            new TextRun({
              text: " Professional Development Resources",
              font: "Arial", 
              size: 20,
              color: "666666",
            }),
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 400 },
          border: {
            bottom: {
              color: "1a73e8",
              space: 1,
              style: "single",
              size: 6,
            },
          },
        })
      );

      // Add title
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: title,
              font: "Arial",
              size: 36,
              bold: true,
              color: "1a73e8",
            }),
          ],
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 300 },
        })
      );

      // Add description
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: description,
              font: "Arial",
              size: 24,
              color: "333333",
            }),
          ],
          spacing: { after: 600 },
          alignment: AlignmentType.CENTER,
        })
      );

      // Process content sections with improved handling
      await processContentElement(contentElement, children);

      // Add footer with proper styling
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "© 2024 Edugogy - Professional Development Resources",
              font: "Arial",
              size: 18,
              color: "666666",
              italics: true,
            }),
          ],
          spacing: { before: 800 },
          alignment: AlignmentType.CENTER,
          border: {
            top: {
              color: "cccccc",
              space: 1,
              style: "single",
              size: 3,
            },
          },
        })
      );

      // Create document with proper fonts and margins
      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: "Arial",
                size: 22,
              },
              paragraph: {
                spacing: { line: 276, lineRule: "auto" },
              },
            },
            heading1: {
              run: {
                font: "Arial",
                size: 32,
                bold: true,
                color: "1a73e8",
              },
              paragraph: {
                spacing: { before: 480, after: 240 },
              },
            },
            heading2: {
              run: {
                font: "Arial", 
                size: 28,
                bold: true,
                color: "333333",
              },
              paragraph: {
                spacing: { before: 360, after: 180 },
              },
            },
            heading3: {
              run: {
                font: "Arial",
                size: 24,
                bold: true,
                color: "555555",
              },
              paragraph: {
                spacing: { before: 240, after: 120 },
              },
            },
          },
        },
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,    // 1 inch
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: children,
        }],
      });

      // Generate and save
      const blob = await Packer.toBlob(doc);
      const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;
      saveAs(blob, fileName);
      toast.success('Professional Word document downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document. Please try again.');
    }
  };

  // Enhanced content element processor
  const processContentElement = async (element: Element, children: any[]) => {
    for (const child of element.children) {
      await processElement(child, children);
    }
  };

  // Enhanced element processor with better handling for gradient cards
  const processElement = async (element: Element, children: any[]) => {
    const tagName = element.tagName;
    const classList = Array.from(element.classList);
    
    // Handle div containers (including gradient cards and sections)
    if (tagName === 'DIV') {
      // Check if this is a main section/card
      if (classList.includes('bg-gradient-card') || classList.includes('border') || classList.includes('rounded-lg')) {
        // Add section header if there's an h2 or h3 inside
        const sectionTitle = element.querySelector('h2, h3');
        if (sectionTitle) {
          const level = sectionTitle.tagName === 'H2' ? HeadingLevel.HEADING_1 : HeadingLevel.HEADING_2;
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: sectionTitle.textContent || '',
                  font: "Arial",
                  size: sectionTitle.tagName === 'H2' ? 32 : 28,
                  bold: true,
                  color: "1a73e8",
                }),
              ],
              heading: level,
              spacing: { before: 480, after: 240 },
              shading: {
                type: ShadingType.CLEAR,
                color: "f8f9fa",
                fill: "f8f9fa",
              },
            })
          );
        }

        // Process radio button groups - only if this is a section with radio inputs
        if (element.querySelector('input[type="radio"]')) {
          await processRadioGroups(element, children);
        }
        // Process checkboxes - only if this is a section with checkboxes and no radio buttons
        else if (element.querySelector('input[type="checkbox"]')) {
          await processCheckboxes(element, children);
        }
        // Process form fields - only if this has labels but no radio/checkbox inputs
        else if (element.querySelector('label') && !element.querySelector('input[type="radio"], input[type="checkbox"]')) {
          await processFormFields(element, children);
        }
        // Process grid layouts
        else if (element.querySelector('.grid')) {
          await processGridLayout(element, children);
        }
        // Process tables
        else if (element.querySelector('table')) {
          const tables = element.querySelectorAll('table');
          for (const table of tables) {
            await processTable(table, children);
          }
        }
        // Process text areas and other content
        else {
          // Process text areas for open-ended questions
          const textAreas = element.querySelectorAll('.border.border-muted-foreground\\/30');
          textAreas.forEach((textArea) => {
            const prevElement = textArea.previousElementSibling;
            if (prevElement?.textContent?.trim()) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: prevElement.textContent.trim(),
                      font: "Arial",
                      size: 22,
                      bold: true,
                      color: "333333",
                    }),
                  ],
                  spacing: { before: 240, after: 120 },
                })
              );
              
              // Add text area placeholder
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "________________________________________________\n________________________________________________\n________________________________________________",
                      font: "Arial",
                      size: 20,
                      color: "666666",
                    }),
                  ],
                  spacing: { after: 240 },
                })
              );
            }
          });
          
          // Process other content recursively only if no special elements were found
          if (!element.querySelector('.border.border-muted-foreground\\/30')) {
            for (const child of element.children) {
              if (!['H2', 'H3'].includes(child.tagName)) {
                await processElement(child, children);
              }
            }
          }
        }
        
        // Add spacing after section
        children.push(new Paragraph({ text: '', spacing: { after: 400 } }));
      }
      
      // Handle other divs recursively
      else {
        await processContentElement(element, children);
      }
    }
    
    // Handle headings
    else if (tagName === 'H2') {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: element.textContent || '',
              font: "Arial",
              size: 32,
              bold: true,
              color: "1a73e8",
            }),
          ],
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 480, after: 240 },
        })
      );
    } else if (tagName === 'H3') {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: element.textContent || '',
              font: "Arial",
              size: 28,
              bold: true,
              color: "333333",
            }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 360, after: 180 }
        })
      );
    }
    
    // Handle paragraphs
    else if (tagName === 'P' && element.textContent?.trim()) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: element.textContent.trim(),
              font: "Arial",
              size: 22,
              color: "333333",
            }),
          ],
          spacing: { after: 180 },
        })
      );
    }
    
    // Handle lists
    else if (tagName === 'UL') {
      const listItems = element.querySelectorAll('li');
      listItems.forEach((item, index) => {
        if (item.textContent?.trim()) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `• ${item.textContent.trim()}`,
                  font: "Arial",
                  size: 22,
                  color: "333333",
                }),
              ],
              spacing: { after: 120 },
              indent: { left: 360 },
            })
          );
        }
      });
      children.push(new Paragraph({ text: '', spacing: { after: 240 } }));
    }
    
    // Handle tables
    else if (tagName === 'TABLE') {
      await processTable(element, children);
    }
  };

  // Process grid layouts (two-column sections)
  const processGridLayout = async (element: Element, children: any[]) => {
    const gridItems = element.querySelectorAll('.border, [class*="border"]');
    for (const item of gridItems) {
      const title = item.querySelector('h3, h4, .font-semibold');
      if (title) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: title.textContent || '',
                font: "Arial",
                size: 24,
                bold: true,
                color: "555555",
              }),
            ],
            spacing: { before: 240, after: 120 },
          })
        );
      }
      
      // Process lists within grid items
      const lists = item.querySelectorAll('ul');
      for (const list of lists) {
        const listItems = list.querySelectorAll('li');
        listItems.forEach((listItem) => {
          if (listItem.textContent?.trim()) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• ${listItem.textContent.trim()}`,
                    font: "Arial",
                    size: 20,
                    color: "333333",
                  }),
                ],
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          }
        });
      }
    }
  };

  // Process form fields and worksheets
  const processFormFields = async (element: Element, children: any[]) => {
    const labels = element.querySelectorAll('label');
    labels.forEach((label) => {
      if (label.textContent?.trim()) {
        // Add label
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: label.textContent.trim(),
                font: "Arial",
                size: 22,
                bold: true,
                color: "333333",
              }),
            ],
            spacing: { before: 180, after: 60 },
          })
        );
        
        // Add fill-in line
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "________________________________________________",
                font: "Arial",
                size: 20,
                color: "666666",
              }),
            ],
            spacing: { after: 180 },
          })
        );
      }
    });
  };

  // Process radio button groups - fixed to avoid repetition and format as tables
  const processRadioGroups = async (element: Element, children: any[]) => {
    // Find direct question containers (divs that contain both question text and radio options)
    const questionContainers = element.querySelectorAll('div > p.font-medium');
    
    questionContainers.forEach((questionText) => {
      const questionContainer = questionText.parentElement;
      if (!questionContainer) return;
      
      // Find the radio options container (next sibling with flex layout)
      const optionsContainer = questionContainer.querySelector('div.flex');
      if (!optionsContainer) return;
      
      const radioLabels = optionsContainer.querySelectorAll('label');
      if (radioLabels.length === 0) return;
      
      // Add question
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: questionText.textContent?.trim() || '',
              font: "Arial",
              size: 22,
              bold: true,
              color: "333333",
            }),
          ],
          spacing: { before: 240, after: 120 },
        })
      );

      // Create table for radio options
      const tableCells: any[] = [];
      radioLabels.forEach((label) => {
        const span = label.querySelector('span');
        if (span?.textContent?.trim()) {
          tableCells.push(
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `○ ${span.textContent.trim()}`,
                      font: "Arial",
                      size: 18,
                      color: "333333",
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                })
              ],
              width: { size: 100 / radioLabels.length, type: WidthType.PERCENTAGE },
              margins: { top: 100, bottom: 100, left: 50, right: 50 },
            })
          );
        }
      });

      if (tableCells.length > 0) {
        children.push(
          new Table({
            rows: [
              new TableRow({
                children: tableCells,
              })
            ],
            width: { size: 100, type: WidthType.PERCENTAGE },
          })
        );
      }

      // Add spacing after question
      children.push(new Paragraph({ text: '', spacing: { after: 240 } }));
    });
  };

  // Process checkboxes
  const processCheckboxes = async (element: Element, children: any[]) => {
    const checkboxItems = element.querySelectorAll('div');
    checkboxItems.forEach((item) => {
      const span = item.querySelector('span');
      if (span?.textContent?.trim() && item.querySelector('input[type="checkbox"]')) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `☐ ${span.textContent.trim()}`,
                font: "Arial",
                size: 20,
                color: "333333",
              }),
            ],
            spacing: { after: 100 },
            indent: { left: 240 },
          })
        );
      }
    });
  };

  // Enhanced table processor
  const processTable = async (table: Element, children: any[]) => {
    const tableRows = [];
    const rows = table.querySelectorAll('tr');
    
    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('th, td');
      const tableCells: any[] = [];
      
      cells.forEach((cell) => {
        const isHeader = cell.tagName === 'TH';
        tableCells.push(
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: cell.textContent?.trim() || '',
                    font: "Arial",
                    size: isHeader ? 22 : 20,
                    bold: isHeader,
                    color: isHeader ? "1a73e8" : "333333",
                  }),
                ],
                alignment: isHeader ? AlignmentType.CENTER : AlignmentType.LEFT,
              })
            ],
            width: { size: 100 / cells.length, type: WidthType.PERCENTAGE },
            shading: isHeader ? {
              type: ShadingType.CLEAR,
              color: "f8f9fa",
              fill: "f8f9fa",
            } : undefined,
          })
        );
      });
      
      tableRows.push(
        new TableRow({
          children: tableCells,
        })
      );
    });

    if (tableRows.length > 0) {
      children.push(
        new Table({
          rows: tableRows,
          width: { size: 100, type: WidthType.PERCENTAGE },
        })
      );
      
      children.push(
        new Paragraph({
          text: '',
          spacing: { after: 400 },
        })
      );
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Screen header */}
      <div className="mb-8 pb-6 border-b-2 border-primary">
        <h1 className="text-3xl font-bold text-primary mb-2">
          {title}
        </h1>
        <p className="text-muted-foreground text-lg">
          {description}
        </p>
        <div className={`flex gap-3 mt-4 ${hideMainDownload ? 'hidden' : ''}`}>
          <Button 
            onClick={handleDownloadWord}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90"
          >
            <Download className="w-4 h-4" />
            Download Word
          </Button>
        </div>
      </div>

      {/* Content */}
      <div id="resource-content">
        {content}
      </div>

      {/* Footer */}
      <div className="mt-12 pt-6 border-t border-muted-foreground/20 text-center text-sm text-muted-foreground">
        <p>© 2024 Edugogy - Professional Development Resources</p>
      </div>
    </div>
  );
};

export default ResourceLayout;